export const buildAnalyticsQueryString = ({
  status = '',
  dateRange = '',
  campaignId = '',
  memberUserId = '',
  period = '',
} = {}) => {
  const params = new URLSearchParams();
  params.set('status', status ?? '');
  params.set('dateRange', dateRange ?? '');
  if (campaignId) params.set('campaignId', campaignId);
  if (memberUserId) params.set('memberUserId', memberUserId);
  if (period) params.set('period', period);
  return params.toString();
};

const formatPeriodLabel = period => {
  if (!period) return '';
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return String(period);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const toLegacyMetricString = value => String(Number(value) || 0);

/**
 * Maps campaign-analytics `/graph` payloads to legacy dashboard `data.results`.
 * Keeps backend unchanged — normalisation happens only in the frontend.
 */
export const adaptGraphResponse = payload => {
  const points = payload?.data?.points;
  if (!Array.isArray(points)) return payload;

  return {
    ...payload,
    message: payload.message || 'Graph data retrieved successfully',
    data: {
      groupingType: payload.data.grouping || payload.data.groupingType || 'monthly',
      daysSinceCreation:
        payload.data.daysSinceCreation
        ?? payload.data.days
        ?? payload.data.range?.days
        ?? 0,
      results: points.map(point => ({
        label: point.label || formatPeriodLabel(point.period),
        period: point.period,
        emailssent: toLegacyMetricString(point.emailsSent),
        emailsopened: toLegacyMetricString(point.opens ?? point.emailsOpened),
        clicks: toLegacyMetricString(point.clicks),
        replies: toLegacyMetricString(point.replies),
        bounced: toLegacyMetricString(point.bounced),
        positivereply: toLegacyMetricString(point.positiveReply ?? point.positivereply),
        opportunity: toLegacyMetricString(point.opportunity),
        unsubscribed: toLegacyMetricString(point.unsubscribed),
      })),
    },
  };
};

/** Maps campaign-analytics top campaigns to legacy dashboard `data` array. */
export const adaptTopCampaignsResponse = payload => {
  const campaigns = payload?.data?.campaigns;
  if (!Array.isArray(campaigns)) return payload;

  return {
    ...payload,
    data: campaigns.map(row => ({
      id: row.campaignId,
      name: row.campaignName,
      total_emails_sent: Number(row.emailsSent) || 0,
      total_replies: Number(row.replies) || 0,
      reply_rate: Number(row.replyRate ?? row.openRate) || 0,
    })),
  };
};

const ACTIVITY_TYPE_MAP = {
  open: 'EMAIL_OPENED',
  click: 'LINK_CLICKED',
};

const buildActivityMessage = activity => {
  const campaign = activity.campaign_name ? `"${activity.campaign_name}"` : 'your campaign';
  if (activity.type === 'open') return `A lead opened an email from ${campaign}`;
  if (activity.type === 'click') return `A lead clicked a link in ${campaign}`;
  return `Activity in ${campaign}`;
};

/** Maps campaign-analytics activities to legacy dashboard `data` array. */
export const adaptActivitiesResponse = payload => {
  const activities = payload?.data?.activities;
  if (!Array.isArray(activities)) return payload;

  return {
    ...payload,
    data: activities.map((activity, index) => ({
      id: activity.id || `${activity.created_at}-${index}`,
      message: buildActivityMessage(activity),
      created_at: activity.created_at,
      activity_type: ACTIVITY_TYPE_MAP[activity.type] || 'EMAIL_SENT',
    })),
  };
};
